/**
 * OwnerDashboardOverview — Redesigned Business Owner overview per the
 * mockup: welcome hero, business card, action-needed callouts, weekly
 * stats strip, quick-actions grid, business-tools list, and the Grow
 * Your Business upsell.
 *
 * All data is passed in from the parent (no fresh fetches) — this keeps
 * the component pure and cheap to reuse in the desktop layout.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Plus, ShoppingBag, Sparkles, Megaphone, Pencil, QrCode,
  Store, Package, Image as ImageIcon, BarChart3, Users, Star,
  CheckCircle2, AlertTriangle, Crown, ChevronRight, TrendingUp,
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";

const QUICK_ACTIONS = [
  { label: "Add Product", tab: "products", icon: Plus, action: "add" },
  { label: "Orders", tab: "orders", icon: ShoppingBag },
  { label: "Create a Drop", tab: "products", icon: Sparkles, action: "drop" },
  { label: "Promote", tab: "featured", icon: Megaphone },
  { label: "Edit Profile", tab: "profile", icon: Pencil },
  { label: "My QR Code", tab: "qr", icon: QrCode },
];

const BUSINESS_TOOLS = [
  { label: "Storefront", tab: "profile", icon: Store },
  { label: "Products", tab: "products", icon: Package },
  { label: "Orders", tab: "orders", icon: ShoppingBag },
  { label: "Blinterest Drops", tab: "products", icon: ImageIcon, action: "drop" },
  { label: "Promotions & Ads", tab: "featured", icon: Megaphone },
  { label: "Analytics", tab: "analytics", icon: BarChart3 },
  { label: "Customers / Leads", tab: "customers", icon: Users },
  { label: "Reviews", tab: "reviews", icon: Star },
];

function greetingFor(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export default function OwnerDashboardOverview({
  business,
  user,
  productCount = 0,
  totalOrders = 0,
  totalRevenue = 0,
  onSelectTab,
}) {
  const greeting = greetingFor(new Date().getHours());
  const firstName = (user?.name || user?.email || "there").split(/[\s@]/)[0];

  // Live stats-strip — real 7-day counts from analytics_events.
  // Falls back to zeros if the endpoint is down; excludes admin visits.
  const [stats, setStats] = useState({ profile_views: 0, product_views: 0, contact_clicks: 0 });
  useEffect(() => {
    const id = business?.business_id;
    if (!id) return;
    let cancel = false;
    axios
      .get(`${API}/analytics/business/${id}/stats-strip?days=7`, { withCredentials: true })
      .then((r) => {
        if (cancel) return;
        setStats({
          profile_views: r.data?.profile_views || 0,
          product_views: r.data?.product_views || 0,
          contact_clicks: r.data?.contact_clicks || 0,
        });
      })
      .catch(() => { /* silent — keeps zeros */ });
    return () => { cancel = true; };
  }, [business?.business_id]);

  // Action items — computed from business state so the list is always
  // truthful. Reordered by urgency so the most valuable next step floats
  // to the top.
  const actionItems = useMemo(() => {
    const items = [];
    if (business && !business.stripe_connect_onboarded && !business.stripe_account_id) {
      items.push({
        id: "payout",
        title: "Complete payout setup",
        sub: "Start accepting online orders.",
        cta: "Set Up",
        icon: TrendingUp,
        tab: "payouts",
        tone: "amber",
      });
    }
    if (productCount === 0) {
      items.push({
        id: "first-product",
        title: "Add your first product",
        sub: "Get your products in front of buyers.",
        cta: "Add Product",
        icon: Package,
        tab: "products",
        tone: "amber",
        action: "add",
      });
    }
    if (business && !business.is_verified) {
      items.push({
        id: "verification",
        title: "Get verified",
        sub: "Build trust with a gold verified badge.",
        cta: "Verify",
        icon: CheckCircle2,
        tab: "profile",
        tone: "navy",
      });
    }
    return items;
  }, [business, productCount]);

  const planLabel = business?.subscription_tier
    ? business.subscription_tier.charAt(0).toUpperCase() + business.subscription_tier.slice(1) + " Plan"
    : "Free Plan";

  return (
    <div className="space-y-6" data-testid="owner-dashboard-overview">
      {/* Welcome hero */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-gold font-semibold">
          {greeting}
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl text-tbc-navy leading-tight mt-1">
          Welcome back, {firstName}.
        </h1>
        <p className="text-sm text-tbc-navy/60 mt-1">
          Here's what's happening with your business today.
        </p>
      </div>

      {/* Business card */}
      {business && (
        <div className="bg-tbc-navy text-white rounded-2xl p-4 sm:p-5 flex items-center gap-4" data-testid="owner-business-card">
          {business.logo_url ? (
            <img
              src={business.logo_url}
              alt={business.name}
              className="w-14 h-14 rounded-xl object-cover flex-none bg-white"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-tbc-gold/20 flex items-center justify-center flex-none">
              <Store className="w-6 h-6 text-tbc-gold" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-heading text-lg leading-tight truncate">{business.name}</p>
            <p className="text-xs text-white/70 mt-0.5 truncate">
              {business.custom_category || business.category}
            </p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {business.is_verified && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-tbc-gold">
                  <CheckCircle2 className="w-3 h-3" /> Verified
                </span>
              )}
              <span className="text-[10px] uppercase tracking-widest text-white/60">{planLabel}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onSelectTab?.("profile")}
            className="text-xs font-semibold text-tbc-gold hover:text-tbc-gold/80 flex items-center gap-1"
            data-testid="owner-business-manage"
          >
            Manage <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Action Needed */}
      {actionItems.length > 0 && (
        <div className="space-y-3" data-testid="owner-action-needed">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-navy/60 font-semibold">
              Action Needed ({actionItems.length})
            </p>
          </div>
          {actionItems.map((item) => {
            const Icon = item.icon;
            const tone = item.tone === "amber"
              ? "bg-amber-50 border-amber-200"
              : "bg-white border-tbc-navy/10";
            return (
              <div
                key={item.id}
                className={`rounded-xl border p-3 flex items-center gap-3 ${tone}`}
                data-testid={`owner-action-${item.id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center flex-none border border-black/5">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-tbc-navy text-sm">{item.title}</p>
                  <p className="text-xs text-tbc-navy/60">{item.sub}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => onSelectTab?.(item.tab, item.action)}
                  className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 text-xs h-8 flex-none"
                >
                  {item.cta} <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* This Week stats strip */}
      <div className="rounded-2xl border border-tbc-navy/10 bg-white p-5" data-testid="owner-stats-strip">
        <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-navy/50 font-semibold mb-3">This Week</p>
        <div className="grid grid-cols-3 gap-3">
          <StatMini value={stats.profile_views} label="Profile Views" />
          <StatMini value={stats.product_views} label="Product Views" />
          <StatMini value={stats.contact_clicks} label="Contact Clicks" />
        </div>
        <button
          type="button"
          onClick={() => onSelectTab?.("analytics")}
          className="mt-4 text-xs font-semibold text-tbc-gold hover:text-tbc-gold/80 flex items-center gap-1"
          data-testid="owner-view-analytics"
        >
          View Analytics <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Quick Actions */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-navy/60 font-semibold mb-3">Quick Actions</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="owner-quick-actions">
          {QUICK_ACTIONS.map((qa) => {
            const Icon = qa.icon;
            return (
              <button
                key={qa.label}
                type="button"
                onClick={() => onSelectTab?.(qa.tab, qa.action)}
                className="rounded-xl border border-tbc-navy/10 bg-white p-4 flex flex-col items-center gap-2 hover:border-tbc-gold hover:shadow-md transition-all"
                data-testid={`owner-quick-${qa.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="w-10 h-10 rounded-full bg-tbc-cream flex items-center justify-center">
                  <Icon className="w-4.5 h-4.5 text-tbc-navy" />
                </div>
                <span className="text-xs font-medium text-tbc-navy text-center leading-tight">{qa.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Your Business Tools */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-navy/60 font-semibold mb-3">Your Business Tools</p>
        <div className="rounded-2xl border border-tbc-navy/10 bg-white overflow-hidden divide-y divide-tbc-navy/5" data-testid="owner-business-tools">
          {BUSINESS_TOOLS.map((tool) => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.label}
                type="button"
                onClick={() => onSelectTab?.(tool.tab, tool.action)}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-tbc-cream/40 transition-colors"
                data-testid={`owner-tool-${tool.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <Icon className="w-4 h-4 text-tbc-navy/70 flex-none" />
                <span className="text-sm text-tbc-navy flex-1">{tool.label}</span>
                <ChevronRight className="w-4 h-4 text-tbc-navy/30 flex-none" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Grow Your Business */}
      {business?.subscription_tier !== "accelerator" && (
        <div className="rounded-2xl bg-gradient-to-br from-tbc-navy to-tbc-navy/85 text-white p-5" data-testid="owner-grow-cta">
          <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-gold font-semibold flex items-center gap-1">
            <Crown className="w-3 h-3" /> Grow Your Business
          </p>
          <h3 className="font-heading text-xl mt-1.5">Upgrade to Accelerator</h3>
          <p className="text-xs text-white/70 mt-1 leading-relaxed">
            Unlock advanced analytics, featured placement, unlimited products, and more.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1 mt-4 rounded-full bg-tbc-gold px-4 py-2 text-xs font-semibold text-tbc-navy hover:bg-tbc-gold/90"
            data-testid="owner-view-plans"
          >
            View Plans <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}

function StatMini({ value, label }) {
  return (
    <div className="text-center">
      <p className="font-heading text-2xl text-tbc-navy leading-none">
        {(value ?? 0).toLocaleString?.() ?? value ?? 0}
      </p>
      <p className="text-[10px] uppercase tracking-wider text-tbc-navy/50 mt-1">{label}</p>
    </div>
  );
}
